import { FormControl, IconButton, InputAdornment, OutlinedInput } from "@mui/material";

import { StyledAppInput } from ".";
import { Error } from "./styles";
import React from "react";
import { EyeCloseIcon, EyeIcon } from "@assets";
import { MAXIMIN_PASSWORD_CHARACTERS, MAXIMIN_255_CHARACTERS } from "@configs";

type IAppInputProps = {
  label?: string;
  value?: string | number;
  name?: string;
  icon?: any;
  error?: any;
  handleChange?: (value: any) => void;
  handleBlur?: (value: any) => void;
  onClick?: (event?: React.MouseEvent<HTMLElement>) => void;
  onChange?: (value: any) => void;
  isPassword?: boolean;
  isSearch?: boolean;
  iconStart?: any;
  touched?: any;
  placeholder?: string;
  disabled?: boolean;
  isNumber?: boolean;
  max?: number;
  min?: number;
  readonly?: boolean;
  step?: number | string;
  style?: React.CSSProperties;
  playwrightName?: string; // Optional prop for Playwright testing
};

export const AppInput = (props: IAppInputProps) => {
  const { label, value, name, error, handleBlur, handleChange, onChange, onClick, iconStart, icon, isPassword, isSearch, touched, placeholder, disabled, isNumber, max, min = 0, readonly = false, step: stepProp, style, playwrightName } = props;

  // component variable
  const [showPassword, setShowPassword] = React.useState(isPassword);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleOnChange = React.useCallback(
    (event: any) => {
      let inputValue = event.target.value;

      // Handle numeric inputs
      if (isNumber && typeof inputValue === "string") {
        let cleanedValue = inputValue;

        // Handle empty string case - set to "0"
        if (cleanedValue === "") {
          cleanedValue = '0';
        }

        // Remove leading zeros but preserve single zero
        if (cleanedValue !== '0') {
          cleanedValue = cleanedValue.replace(/^0+/, '');
          cleanedValue = (cleanedValue === '' ? '0' : cleanedValue);
          cleanedValue = (cleanedValue.startsWith('.') ? '0' + cleanedValue : cleanedValue);
        }

        // Update the event target value to reflect the cleaned value
        event.target.value = cleanedValue;

        // Pass the cleaned value to handleChange
        handleChange && handleChange(cleanedValue);
      } else {
        // Non-numeric inputs - pass original value
        handleChange && handleChange(inputValue);
      }

      // Call additional onChange if provided
      onChange && onChange(event);
    },
    [handleChange, onChange, isNumber]
  );

  const determineStep = () => {
    if (!isNumber) {
      return undefined; 
    }
    if (stepProp !== undefined) {
      return String(stepProp);
    }
    return "0.5";
  };

  const combinedStyle: React.CSSProperties = {
    border: "none",
    ...style,
  };

  // For display value, handle numeric inputs properly
  // Stopped using because it can show one value, like 5, but actually hold a string like "05"
  const getDisplayValue = () => {
    if (isNumber) {
      // Handle empty, null, undefined cases - default to "0"
      if (value === "" || value === null || value === undefined) {
        return "0";
      }
      // Convert to string to avoid React warnings about controlled components
      return String(value);
    }
    return value;
  };

  return (
    <StyledAppInput 
      iconStart={iconStart}
      data-playwright={playwrightName}
    >
      <div className="input__wrapper">
        {label && <div className="input__label">{label}</div>}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <OutlinedInput
            style={combinedStyle}
            //value={getDisplayValue()}
            value={value}
            onChange={handleOnChange}
            onBlur={handleBlur}
            name={name}
            type={isNumber ? "number" : showPassword ? "password" : isSearch ? "search" : "text"}
            inputMode={isNumber ? "numeric" : undefined}
            autoComplete="off"
            placeholder={placeholder}
            sx={{
              "& .MuiOutlinedInput-root:hover": {
                "& > fieldset": {
                  borderColor: "orange",
                },
              },
            }}
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                onClick && onClick();
              }
            }}
            startAdornment={
              <>
                {iconStart && (
                  <InputAdornment position="start">
                    <IconButton edge="end" disabled={disabled} onClick={onClick} disableRipple={true} disableFocusRipple={true}>
                      {iconStart}
                    </IconButton>
                  </InputAdornment>
                )}
              </>
            }
            endAdornment={
              <InputAdornment position="end">
                {isPassword && (
                  <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
                    {showPassword ? <EyeIcon /> : <EyeCloseIcon />}
                  </IconButton>
                )}
                {icon && <IconButton edge="end">{icon}</IconButton>}
              </InputAdornment>
            }
            aria-describedby="outlined-weight-helper-text"
            inputProps={{
              "aria-label": playwrightName || name,
              maxLength: isPassword ? MAXIMIN_PASSWORD_CHARACTERS : MAXIMIN_255_CHARACTERS,
              max: max,
              min: min,
              step: determineStep(),
              pattern: isNumber ? "[0-9]+(\\.[0-9]{1,2})?" : "",
            }}
            fullWidth
            readOnly={readonly}
            disabled={disabled}
          />
          {/* show error message only when change input value */}
          {!!error && !!touched && <Error>{error}</Error>}
        </FormControl>
      </div>
    </StyledAppInput>
  );
};